import { FieldErrors, UseFormRegister } from "react-hook-form";
import get from "lodash.get";
import type { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";

import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { isValidPhone, maskPhone, normalizePhone } from "@/utils/phone";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form.tsx";

interface FormInputProps {
  name: string;
  label?: string;
  placeholder: string;
  type?: "text" | "textarea" | "email";
  variant?: "flat" | "bordered" | "faded" | "underlined";
  color?:
    | "default"
    | "primary"
    | "secondary"
    | "success"
    | "warning"
    | "danger";
  size?: "sm" | "md" | "lg";
  radius?: "none" | "sm" | "md" | "lg" | "full";
  isRequired?: boolean;
  disabled?: boolean;
  className?: string;
  description?: string;
  /**
   * A phone number: only digits can be typed or pasted, a `+1` country code is
   * dropped, and the form refuses to submit anything but exactly 10 digits.
   */
  phone?: boolean;
  formFieldParams?: {
    register: UseFormRegister<any>;
    errors: FieldErrors<any>;
    loading: boolean;
    watch: (field: string) => any;
  };
  form?: any;
}

/**
 * The digits a phone field holds after an edit of its masked value. Deleting
 * only a dash of the mask removes the digit before it instead, otherwise the
 * mask would put the dash straight back and backspace would seem stuck.
 */
const phoneDigitsAfterEdit = (
  event: ChangeEvent<HTMLInputElement>,
  previous: string | null | undefined,
): string => {
  const typed = event.target.value;
  const digits = normalizePhone(typed);
  const before = normalizePhone(previous);

  if (digits !== before || typed.length >= maskPhone(before).length) {
    return digits;
  }

  const caret = event.target.selectionStart ?? typed.length;
  const digitsBeforeCaret = typed.slice(0, caret).replace(/\D/g, "").length;

  if (digitsBeforeCaret === 0) {
    return digits;
  }

  return digits.slice(0, digitsBeforeCaret - 1) + digits.slice(digitsBeforeCaret);
};

export const FormInput = ({
  name,
  className,
  label,
  variant = "bordered",
  color = "default",
  type = "text",
  radius = "sm",
  size = "sm",
  placeholder,
  description = "",
  isRequired = false,
  disabled = false,
  phone = false,
  form = null,
  formFieldParams,
  ...rest
}: FormInputProps) => {
  const { t } = useTranslation();
  const errors = [];
  const shortName = name?.split(".").pop();

  let value = "";

  if (formFieldParams) {
    value = formFieldParams.watch(name);
  }

  if (
    shortName &&
    formFieldParams &&
    typeof formFieldParams?.errors[shortName]?.message !== "undefined"
  ) {
    errors.push(formFieldParams?.errors[shortName]?.message);
  }
  if (formFieldParams) {
    const subError = get(formFieldParams?.errors, name);

    if (subError) {
      errors.push(subError.message);
    }
  }

  if (form !== null) {
    return (
      <FormField
        control={form.control}
        name={name}
        rules={
          phone
            ? {
                validate: (current: string | null | undefined) =>
                  !current ||
                  isValidPhone(normalizePhone(current)) ||
                  t("validation.phone", "Enter a 10-digit phone number, e.g. 5148257340"),
              }
            : undefined
        }
        render={({ field }) => (
          <FormItem className={className}>
            {label && (
              <FormLabel className="gap-1">
                {label}{" "}
                {isRequired && <span className="text-destructive">*</span>}
              </FormLabel>
            )}
            <FormControl>
              <Input
                placeholder={placeholder}
                {...field}
                {...(phone
                  ? {
                      type: "tel",
                      inputMode: "numeric" as const,
                      autoComplete: "tel-national",
                      value: maskPhone(field.value),
                      onChange: (event: ChangeEvent<HTMLInputElement>) =>
                        field.onChange(phoneDigitsAfterEdit(event, field.value)),
                    }
                  : {})}
                disabled={disabled}
              />
            </FormControl>
            {description && <FormDescription>{description}</FormDescription>}
            <FormMessage />
          </FormItem>
        )}
      />
    );
  }

  return (
    <Field className={className}>
      {label && <FieldLabel htmlFor={name}>{label}</FieldLabel>}
      <Input
        id={name}
        value={value ?? ""}
        required={!!isRequired}
        placeholder={placeholder}
        disabled={disabled}
        {...rest}
      />
      {description && <FieldDescription>{description}</FieldDescription>}
    </Field>
  );

  // return (
  //   <div className={className}>
  //     <div
  //       className={"text-xs leading-4 font-medium mb-2 text-zinc-400 uppercase"}
  //     >
  //       {label ?? ""}
  //     </div>
  //     <Input
  //       classNames={{
  //         inputWrapper: variant === "underlined" ? "border-b" : "border",
  //       }}
  //       value={value ?? ""}
  //       {...formFieldParams?.register(name)}
  //       color={color}
  //       description={description}
  //       errorMessage={errors[0]?.toString().replace(/<[^>]*>/g, "")}
  //       isInvalid={!!errors.length}
  //       isRequired={isRequired}
  //       placeholder={placeholder}
  //       radius={radius}
  //       size={size}
  //       type={type}
  //       variant={variant}
  //     />
  //   </div>
  // );
};
